<template>
  <div>
    <sidebar></sidebar>
    <div :style="{ 'margin-left': sidebarwidth }">
      <div class="px-5 py-5">

        <!-- Header -->
        <div class="flex justify-between items-center mb-5">
          <div>
            <h2 class="text-2xl font-bold">Role Management</h2>
            <p class="text-sm text-gray-500 mt-1">Create roles and assign permissions to them.</p>
          </div>
          <button
            v-if="can('add_role_master')"
            @click="openAddRoleModal"
            class="bg-black text-white px-4 py-2 rounded text-sm font-semibold hover:bg-gray-800"
          >+ New Role</button>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="py-20 text-center text-gray-400">Loading roles...</div>

        <!-- Roles Table -->
        <div v-else class="bg-white rounded-lg shadow overflow-hidden">
          <table class="w-full text-sm text-left">
            <thead class="text-xs text-white uppercase bg-black">
              <tr>
                <th class="py-3 px-4">Role Name</th>
                <th class="py-3 px-4">Permissions</th>
                <th class="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="roles.length === 0">
                <td colspan="3" class="py-10 text-center text-gray-400">No roles found.</td>
              </tr>
              <tr
                v-for="role in roles"
                :key="role.id"
                class="border-b odd:bg-white even:bg-gray-50"
              >
                <td class="py-4 px-4 font-medium">{{ role.role_name }}</td>
                <td class="py-4 px-4">
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="perm in role.permissions"
                      :key="perm.id"
                      class="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded border border-blue-100"
                    >{{ perm.codename }}</span>
                    <span v-if="role.permissions.length === 0" class="text-gray-400 text-xs">No permissions</span>
                  </div>
                </td>
                <td class="py-4 px-4">
                  <div class="flex justify-end gap-2">
                    <button
                      v-if="can('change_permission')"
                      @click="openPermissionModal(role)"
                      class="text-xs px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700"
                    >Manage Permissions</button>
                    <button
                      v-if="can('change_role_master') && role.role_name.toLowerCase() !== 'superadmin'"
                      @click="openEditRoleModal(role)"
                      class="text-xs px-3 py-1.5 rounded bg-gray-700 text-white hover:bg-gray-800"
                    >Edit</button>
                    <button
                      v-if="can('delete_role_master') && role.role_name.toLowerCase() !== 'superadmin'"
                      @click="confirmDeleteRole(role)"
                      class="text-xs px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700"
                    >Delete</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ── ADD / EDIT ROLE MODAL ──────────────────────────────── -->
    <div v-if="showRoleModal" class="popupContainer">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
        <h3 class="font-bold text-lg mb-4">{{ isEditRoleMode ? 'Edit Role' : 'New Role' }}</h3>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Role Name <span class="text-red-500">*</span></label>
          <input
            v-model="roleForm.role_name"
            type="text"
            placeholder="e.g. Auditor"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
          />
        </div>
        <p v-if="roleError" class="text-red-500 text-sm mb-3">{{ roleError }}</p>
        <div class="flex justify-end gap-3">
          <button @click="showRoleModal = false" class="px-4 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50">Cancel</button>
          <button
            @click="saveRole"
            :disabled="savingRole"
            class="px-4 py-2 rounded text-sm text-white font-semibold bg-black hover:bg-gray-800 disabled:opacity-60"
          >{{ savingRole ? 'Saving...' : (isEditRoleMode ? 'Update' : 'Create') }}</button>
        </div>
      </div>
    </div>

    <!-- ── DELETE ROLE CONFIRMATION ───────────────────────────── -->
    <div v-if="showDeleteRoleModal" class="popupContainer">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
        <h3 class="font-bold text-lg mb-2">Delete Role</h3>
        <p class="text-sm text-gray-600 mb-1">Are you sure you want to delete:</p>
        <p class="font-bold text-red-600 mb-5">{{ deleteRoleTarget?.role_name }}</p>
        <p class="text-xs text-gray-400 mb-5">This will fail if users are assigned to this role.</p>
        <div class="flex justify-end gap-3">
          <button @click="showDeleteRoleModal = false" class="px-4 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50">Cancel</button>
          <button
            @click="deleteRole"
            :disabled="deletingRole"
            class="px-4 py-2 rounded text-sm text-white font-semibold bg-red-600 hover:bg-red-700 disabled:opacity-60"
          >{{ deletingRole ? 'Deleting...' : 'Yes, Delete' }}</button>
        </div>
      </div>
    </div>

    <!-- ── PERMISSION ASSIGNMENT MODAL ───────────────────────── -->
    <div v-if="showPermModal" class="popupContainer" @click.self="showPermModal = false">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
        <div class="px-6 py-4 border-b">
          <h3 class="font-bold text-lg">Manage Permissions — <span class="text-blue-600">{{ selectedRole?.role_name }}</span></h3>
          <p class="text-xs text-gray-400 mt-1">Check the permissions you want to assign to this role.</p>
        </div>

        <div class="flex-1 overflow-y-auto px-6 py-4">
          <div v-if="loadingPerms" class="py-10 text-center text-gray-400">Loading permissions...</div>
          <div v-else>
            <label class="flex items-center gap-2 mb-4 text-sm font-medium text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                :checked="allPermissionsSelected"
                @change="toggleSelectAllPermissions"
                class="cursor-pointer"
              />
              <span>Select all permissions</span>
            </label>
            <div
              v-for="(perms, model) in groupedPermissions"
              :key="model"
              class="mb-5"
            >
              <p class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{{ model }}</p>
              <div class="grid grid-cols-2 gap-2">
                <label
                  v-for="perm in perms"
                  :key="perm.id"
                  class="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50 border border-gray-100"
                >
                  <input
                    type="checkbox"
                    :value="perm.id"
                    v-model="selectedPermIds"
                    class="cursor-pointer"
                  />
                  <div>
                    <p class="text-sm font-medium text-gray-800">{{ perm.codename }}</p>
                    <p class="text-xs text-gray-400">{{ perm.name }}</p>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="px-6 py-4 border-t flex justify-between items-center">
          <span class="text-xs text-gray-400">{{ selectedPermIds.length }} permission(s) selected</span>
          <div class="flex gap-3">
            <button @click="showPermModal = false" class="px-4 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50">Cancel</button>
            <button
              @click="savePermissions"
              :disabled="savingPerms"
              class="px-4 py-2 rounded text-sm text-white font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
            >{{ savingPerms ? 'Saving...' : 'Save Permissions' }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast feedback -->
    <div v-if="toastMsg" class="fixed bottom-5 right-5 z-50 bg-white border-l-4 shadow-lg rounded px-5 py-3 text-sm max-w-sm"
      :class="toastType === 'error' ? 'border-red-500' : 'border-green-500'">
      <p class="font-semibold text-gray-800">{{ toastTitle }}</p>
      <p class="text-gray-500 text-xs mt-0.5">{{ toastMsg }}</p>
    </div>

  </div>
</template>

<script setup>
import { computed, ref, onMounted } from "vue";
import sidebar from "@/components/sidebar/Sidebar.vue";
import { sidebarwidth } from "@/store/index.js";
import api from "../../boot/axios.js";
import { can } from "@/auth/permissions";
import {
  BUSINESS_PERMISSION_MODELS,
  SYSTEM_PERMISSION_IGNORE_LIST,
} from "@/config/permissionMatrix";

// ── STATE ───────────────────────────────────────────────────────────────────
const roles   = ref([]);
const loading = ref(false);

// Role modal
const showRoleModal  = ref(false);
const isEditRoleMode = ref(false);
const editRoleId     = ref(null);
const roleForm       = ref({ role_name: "" });
const roleError      = ref("");
const savingRole     = ref(false);

// Delete role
const showDeleteRoleModal = ref(false);
const deleteRoleTarget    = ref(null);
const deletingRole        = ref(false);

// Permission modal
const showPermModal     = ref(false);
const selectedRole      = ref(null);
const loadingPerms      = ref(false);
const groupedPermissions = ref({});
const selectedPermIds   = ref([]);
const hiddenPermIds     = ref([]);
const savingPerms       = ref(false);
const allPermissionIds  = ref([]);
const allPermissionsSelected = computed(() => allPermissionIds.value.length > 0 && selectedPermIds.value.length === allPermissionIds.value.length);

const filterBusinessPermissionGroups = (permissionsByModel = {}) => {
  const allowedModels = new Set(BUSINESS_PERMISSION_MODELS.map((m) => String(m).toLowerCase()));
  const ignoredPermissions = new Set(SYSTEM_PERMISSION_IGNORE_LIST);
  const groupedByBusinessModel = {};

  const allPermissions = Object.values(permissionsByModel)
    .flat()
    .filter((perm) => perm && typeof perm === "object");

  allPermissions.forEach((perm) => {
    const code = typeof perm.codename === "string" ? perm.codename : "";
    if (!code || ignoredPermissions.has(code)) {
      return;
    }

    const parts = code.split("_");
    if (parts.length < 2) {
      return;
    }

    const modelName = parts.slice(1).join("_").toLowerCase();
    if (!allowedModels.has(modelName)) {
      return;
    }

    if (!groupedByBusinessModel[modelName]) {
      groupedByBusinessModel[modelName] = [];
    }

    groupedByBusinessModel[modelName].push(perm);
  });

  const filtered = {};
  BUSINESS_PERMISSION_MODELS.forEach((model) => {
    const list = groupedByBusinessModel[model] || [];
    if (list.length > 0) {
      filtered[model] = list;
    }
  });

  return filtered;
};

// Toast
const toastMsg   = ref("");
const toastTitle = ref("");
const toastType  = ref("success");
let toastTimer   = null;

// ── FETCH ROLES ─────────────────────────────────────────────────────────────
const fetchRoles = async () => {
  loading.value = true;
  try {
    const res = await api.get("/auth/role/");
    roles.value = res.data?.results ?? (Array.isArray(res.data) ? res.data : []);
  } catch {
    showToast("error", "Error", "Failed to load roles.");
  } finally {
    loading.value = false;
  }
};

// ── ADD ROLE ────────────────────────────────────────────────────────────────
const openAddRoleModal = () => {
  isEditRoleMode.value = false;
  editRoleId.value     = null;
  roleForm.value       = { role_name: "" };
  roleError.value      = "";
  showRoleModal.value  = true;
};

const openEditRoleModal = (role) => {
  isEditRoleMode.value = true;
  editRoleId.value     = role.id;
  roleForm.value       = { role_name: role.role_name };
  roleError.value      = "";
  showRoleModal.value  = true;
};

const saveRole = async () => {
  if (!roleForm.value.role_name.trim()) { roleError.value = "Role name is required."; return; }
  savingRole.value = true;
  roleError.value  = "";
  try {
    if (isEditRoleMode.value) {
      const res = await api.put(`/auth/role/${editRoleId.value}/`, roleForm.value);
      if (res.status === 200) {
        showToast("success", "Updated", `Role "${roleForm.value.role_name}" updated.`);
        showRoleModal.value = false;
        fetchRoles();
      }
    } else {
      const res = await api.post("/auth/role/", roleForm.value);
      if (res.status === 201) {
        showToast("success", "Created", `Role "${roleForm.value.role_name}" created.`);
        showRoleModal.value = false;
        fetchRoles();
      }
    }
  } catch (err) {
    roleError.value = err?.response?.data?.message || "Failed to save role.";
  } finally {
    savingRole.value = false;
  }
};

// ── DELETE ROLE ─────────────────────────────────────────────────────────────
const confirmDeleteRole = (role) => {
  deleteRoleTarget.value    = role;
  showDeleteRoleModal.value = true;
};

const deleteRole = async () => {
  deletingRole.value = true;
  try {
    const res = await api.delete(`/auth/role/${deleteRoleTarget.value.id}/`);
    if (res.status === 200 && res.data.status === true) {
      showToast("success", "Deleted", res.data.message);
      showDeleteRoleModal.value = false;
      deleteRoleTarget.value    = null;
      fetchRoles();
    } else {
      showToast("error", "Cannot Delete", res.data.message || "Delete failed.");
      showDeleteRoleModal.value = false;
    }
  } catch (err) {
    showToast("error", "Error", err?.response?.data?.message || "Delete failed.");
    showDeleteRoleModal.value = false;
  } finally {
    deletingRole.value = false;
  }
};

// ── PERMISSION MODAL ────────────────────────────────────────────────────────
const openPermissionModal = async (role) => {
  selectedRole.value   = role;
  selectedPermIds.value = role.permissions.map((p) => p.id);
  showPermModal.value  = true;
  loadingPerms.value   = true;
  groupedPermissions.value = {};

  try {
    const res = await api.get("/auth/permissions/");
    groupedPermissions.value = filterBusinessPermissionGroups(res.data.permissions_by_model || {});
    allPermissionIds.value = Object.values(groupedPermissions.value).flat().map((perm) => perm.id);
    const visibleSet = new Set(allPermissionIds.value);
    hiddenPermIds.value   = selectedPermIds.value.filter((id) => !visibleSet.has(id));
    selectedPermIds.value = selectedPermIds.value.filter((id) =>  visibleSet.has(id));
  } catch {
    showToast("error", "Error", "Failed to load permissions.");
    showPermModal.value = false;
  } finally {
    loadingPerms.value = false;
  }
};

const savePermissions = async () => {
  savingPerms.value = true;
  try {
    const res = await api.post(
      `/auth/role/${selectedRole.value.id}/assign-permissions/`,
      { permission_ids: [...selectedPermIds.value, ...hiddenPermIds.value] }
    );
    if (res.data.status === true) {
      showToast("success", "Saved", `Permissions updated for "${selectedRole.value.role_name}".`);
      showPermModal.value = false;
      fetchRoles();
    } else {
      showToast("error", "Error", res.data.message || "Failed to save permissions.");
    }
  } catch {
    showToast("error", "Error", "Failed to save permissions.");
  } finally {
    savingPerms.value = false;
  }
};

const toggleSelectAllPermissions = () => {
  selectedPermIds.value = allPermissionsSelected.value ? [] : [...allPermissionIds.value];
};

// ── TOAST ────────────────────────────────────────────────────────────────────
const showToast = (type, title, msg) => {
  toastType.value  = type;
  toastTitle.value = title;
  toastMsg.value   = msg;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toastMsg.value = ""; }, 3500);
};

// ── INIT ─────────────────────────────────────────────────────────────────────
onMounted(fetchRoles);
</script>

<style scoped>
.popupContainer {
  position: fixed;
  top: 0; bottom: 0; left: 0; right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 50;
}
</style>